import { useEffect, useRef, useCallback } from "react";
import type {
  TravelHealthSnapshot,
  TravelHealthPhase,
  Tier1Snapshot,
  Tier1SelectionSnapshot,
  Tier2Snapshot,
} from "./types";
import { serializeToTemplate, serializeToSections } from "./serialize";
import {
  addConsultationTemplate,
  updateConsultationTemplate,
} from "@/dispatchers/patientDispatchers";
import { getAuthHeader } from "@/services/authToken";
import config from "@/config";

const DEBOUNCE_MS = 1000;

interface AutoSaveArgs {
  consultationId: string;
  companyId: string;
  hasExistingTemplate: boolean;
  phase: TravelHealthPhase;
  tier1Snapshot: Tier1Snapshot | null;
  tier1SelectionSnapshot: Tier1SelectionSnapshot | null;
  tier2Snapshot: Tier2Snapshot | null;
}

function buildSnapshot(args: AutoSaveArgs): TravelHealthSnapshot {
  return {
    version: 1,
    savedAt: new Date().toISOString(),
    phase: args.phase,
    tier1: args.tier1Snapshot ?? undefined,
    tier1Selection: args.tier1SelectionSnapshot ?? undefined,
    tier2: args.tier2Snapshot ?? undefined,
  };
}

export function useTravelHealthAutoSave(args: AutoSaveArgs) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSavedOnce = useRef(false);
  const latestArgsRef = useRef(args);
  latestArgsRef.current = args;

  const doSave = useCallback(async () => {
    const a = latestArgsRef.current;
    if (!a.consultationId) return;
    if (!a.tier1Snapshot && !a.tier1SelectionSnapshot && !a.tier2Snapshot) return;

    const snapshot = buildSnapshot(a);

    try {
      if (!hasSavedOnce.current && !a.hasExistingTemplate) {
        const template = serializeToTemplate(
          snapshot,
          "Travel Health",
          a.companyId,
        );
        await addConsultationTemplate({
          consultationId: a.consultationId,
          template,
        });
      } else {
        const sections = serializeToSections(snapshot);
        await updateConsultationTemplate({
          consultationId: a.consultationId,
          payload: sections,
        });
      }
      hasSavedOnce.current = true;
    } catch (err) {
      console.error("[TravelHealthAutoSave] save failed:", err);
    }
  }, []);

  useEffect(() => {
    if (!args.consultationId) return;
    if (!args.tier1Snapshot && !args.tier1SelectionSnapshot && !args.tier2Snapshot) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      doSave();
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [args.phase, args.tier1Snapshot, args.tier1SelectionSnapshot, args.tier2Snapshot, doSave]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const a = latestArgsRef.current;
      if (!a.consultationId) return;
      if (!a.tier1Snapshot && !a.tier1SelectionSnapshot && !a.tier2Snapshot) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const snapshot = buildSnapshot(a);
      const sections = serializeToSections(snapshot);
      const baseUrl = config.localurl || "";
      const url = `${baseUrl}/PatientConsultation/update-consultation-template?consultationID=${a.consultationId}`;
      const authHeader = getAuthHeader();

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (authHeader) headers["Authorization"] = authHeader;

        fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(sections),
          keepalive: true,
        });
      } catch {
        // best-effort
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
}
